//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import { theme } from '@bvengineering/unison-core';
import { BaseEmoji, Picker } from 'emoji-mart';
import React, { forwardRef } from 'react';

// Imports the necessary styling for the Emoji Picker
import 'emoji-mart/css/emoji-mart.css';

import styled from 'styled-components';

import ActionButton from '@components/atoms/action-button/ActionButton';
import Popover from '@components/molecules/popover/Popover';
import { ITrackableComponent } from '@utils/withTracking';

interface IOwnProps {
  className?: string;
  isDisabled?: boolean;
  onSelect: (nativeEmoji: string) => void;
}

export type Props = IOwnProps &
  Pick<
    React.ComponentProps<typeof Popover>,
    | 'onTogglePopover'
    | 'appendTarget'
    | 'connectPopover'
    | 'isStatic'
    | 'placement'
  > &
  Pick<
    React.ComponentProps<typeof ActionButton>,
    'size' | 'label' | 'iconColor'
  > &
  Partial<ITrackableComponent>;

/**
 * The EmojiPicker atom renders an ActionButton that opens an emoji picker on
 * click. On selecting an emoji, the picker passes back the native string representation
 * of the emoji, which can be inserted directly into a string displayed elsewhere.
 */
const EmojiPicker = forwardRef<HTMLButtonElement, Props>(
  (
    {
      appendTarget = 'parent',
      className = '',
      connectPopover = () => undefined,
      isDisabled = false,
      placement = 'right-start',
      isStatic = false,
      label = '',
      onSelect,
      onTogglePopover,
      size = 'small',
      iconColor,
      trackingId,
    },
    ref,
  ) => {
    const internalOnSelect = (emoji: BaseEmoji) => {
      onSelect(emoji.native);
      // closePopover();
    };

    /**
     * Both the 'emoji' and 'title' props have defaults, so we need to explicitly
     * pass empty strings to stop them from being displayed.
     */
    const popoverContent = (
      <PickerOverrides>
        <Picker
          emoji=''
          title=''
          showPreview={false}
          color={theme.colors.aliases.primary}
          onSelect={internalOnSelect}
        />
      </PickerOverrides>
    );

    return (
      <div className={className}>
        <Popover
          appendTarget={appendTarget}
          connectPopover={connectPopover}
          content={popoverContent}
          isDisabled={isDisabled}
          isStatic={isStatic}
          shouldShowArrow={false}
          shouldOpenOnHover={false}
          placement={placement}
          target={
            <ActionButton
              isDisabled={isDisabled}
              iconColor={iconColor}
              iconName='smile'
              label={label}
              trackingId={trackingId}
              ref={ref}
              size={size}
            />
          }
          onTogglePopover={onTogglePopover}
        />
      </div>
    );
  },
);

/**
 * The Emoji-Mart picker doesn't take a className props, so we use our own wrapper div to apply minor
 * style overrides so that the Emoji-Mart picker blends in smoothly with the Curalate styles.
 */
const PickerOverrides = styled.div`
  .emoji-mart {
    border: unset;
    border-radius: unset;
    font-family: unset;
    font-size: unset;
  }
  .emoji-mart-emoji:focus {
    outline: none;
  }
`;

EmojiPicker.displayName = 'EmojiPicker';
export default EmojiPicker;
